import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AddIcon from '@mui/icons-material/Add'
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    LinearProgress,
} from '@mui/material'
import { studentApiSlice } from '../../../../features/student/studentSlice'
import { groupApiSlice } from '../../../../features/group/groupSlice'
import NoGroupStudentsTable from './NoGroupStudentsTable'
import Sentinel from '../../../../components/infiniteScroll/Sentinel'
import { usePaginatedList } from '../../../../app/hooks/usePaginatedList'

const AddGroupStudentDialog = () => {
    const { formatMessage } = useIntl()
    const { groupId } = useParams()
    const [openDialog, setOpenDialog] = useState(false)
    const [studentsInfo, setStudentsInfo] = useState([])
    const [fetchStudents] =
        groupApiSlice.useLazyFetchAllGroupStudentsNoGroupQuery()

    const {
        rows,
        isLoading: isFetchingStudents,
        loadedPages,
        sentinelRef,
    } = usePaginatedList(
        (page, size) =>
            fetchStudents({
                groupId: Number(groupId),
                params: `page=${page}&size=${size}`,
            }).unwrap(),
        [groupId, openDialog],
        { skip: !openDialog },
    )
    const [getStudentsInfoByIds, { isLoading: isLoadingStudentsInfo }] =
        studentApiSlice.useGetStudentsInfoByIdsMutation()
    const [addStudentToGroup, { isLoading: isAddingStudent }] =
        groupApiSlice.useAddStudentToGroupMutation()

    const getStudentsInfo = async () => {
        try {
            const result = await getStudentsInfoByIds(
                rows.map((student) => student.userId),
            ).unwrap()
            setStudentsInfo(result)
        } catch (error) {}
    }

    const handleAddStudentToGroup = async (courseUserEnrollId: number, courseSubjectId: number) => {
        try {
            await addStudentToGroup({
                courseUserEnrollId,
                studentGroupId: Number(groupId),
                courseSubjectId
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
        } catch (error) {}
    }

    const handleClickOpen = () => {
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
    }

    useEffect(() => {
        if (rows.length) {
            getStudentsInfo()
        }
    }, [rows])

    return (
        <div>
            <Button
                onClick={handleClickOpen}
                variant="contained"
                endIcon={<AddIcon />}
            >
                {formatMessage({ id: 'addStudent' })}
            </Button>

            <Dialog
                maxWidth={'md'}
                fullWidth
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'addStudent' })}
                </DialogTitle>

                <DialogContent>
                    {(isFetchingStudents && loadedPages === 0) ||
                    isAddingStudent ? (
                        <LinearProgress />
                    ) : (
                        <>
                            <NoGroupStudentsTable
                                studentsData={rows}
                                studentsInfo={studentsInfo}
                                isFetchingStudents={isFetchingStudents}
                                isLoadingStudentsInfo={isLoadingStudentsInfo}
                                isAddingStudent={isAddingStudent}
                                addStudentToGroup={handleAddStudentToGroup}
                            />

                            <Sentinel
                                ref={sentinelRef}
                                isLoading={isFetchingStudents}
                            />
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default AddGroupStudentDialog
